import type { StrategyContent, StrategyToolKey } from '@ads/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSaveTool, useTool } from './queries';

/* ─────────────────────────────────────────────────────────────────────
   O rascunho de uma ferramenta do Strategy: a tela edita uma cópia local
   e ela se salva sozinha ~0,8 s depois da última mudança (e ao sair).
   ───────────────────────────────────────────────────────────────────── */

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';
const SAVE_MS = 800;

export function useToolDraft<K extends StrategyToolKey>(key: K) {
  const tool = useTool(key);
  const save = useSaveTool(key);
  const [local, setLocal] = useState<StrategyContent<K> | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pending = useRef<StrategyContent<K> | null>(null);

  useEffect(() => { if (tool.data && !local) setLocal(tool.data.content); }, [tool.data, local]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setSaveState('saving');
    save.mutate(next, {
      onSuccess: () => setSaveState(s => (pending.current ? s : 'saved')),
      onError: () => { setSaveState('error'); pending.current ??= next; },
    });
  }, [save]);

  const commit = useCallback((next: StrategyContent<K>) => {
    setLocal(next);
    pending.current = next;
    setSaveState('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_MS);
  }, [flush]);

  useEffect(() => () => { flush(); }, [flush]);

  const status = saveState === 'saving' ? 'Salvando…' : saveState === 'saved' ? 'Salvo' : saveState === 'error' ? 'Não salvou — tentando de novo' : '';
  return { tool, local, setLocal, commit, flush, saveState, status };
}
